import { Box, Typography } from '@mui/material';

/**
 * A permanent strip naming the environment you are looking at.
 *
 * WHY (2026-08-07). A second Railway deployment now serves the same app from the
 * `staging` branch, and — a deliberate, cost-driven decision — it points at the
 * SAME Supabase database as production. So the two sites are pixel-identical and
 * edit identical data. Without a marker, the only way to tell them apart is
 * reading the address bar, which nobody does after the first five minutes.
 *
 * That matters more here than in a normal staging setup: on staging you are
 * editing REAL people, itineraries and relationships. Someone trying the new
 * Remove button "just to see what it does" removes a real relationship.
 *
 * The banner cannot prevent that. It exists so nobody can claim afterwards that
 * they thought they were somewhere safe.
 *
 * OFF UNLESS ASKED FOR. Renders nothing when `VITE_ENV_LABEL` is unset, which is
 * production's state — so this ships to both branches and only the environment
 * variable differs. Nothing to remember at merge time, and no chance of the
 * banner appearing in front of the client because someone forgot to strip it.
 *
 * Build-time, like every VITE_ variable: setting it needs a redeploy, not a
 * restart.
 */

/** Long labels would wrap and eat the page, so keep it to a word or two. */
const MAX_LABEL = 32;

export const readEnvLabel = (
    raw: string | undefined
): { label: string; sharesProdData: boolean } | null => {
    const label = (raw ?? '').trim().slice(0, MAX_LABEL);
    if (!label) return null;
    return {
        label: label.toUpperCase(),
        // Staging shares the production database today. If that ever stops being
        // true, drop the word from the variable and the warning goes with it.
        sharesProdData: /staging|shared|prod.?data/i.test(label),
    };
};

export const EnvironmentBanner = () => {
    const env = readEnvLabel(import.meta.env.VITE_ENV_LABEL);
    if (!env) return null;

    return (
        <Box
            role="status"
            aria-label={`${env.label} environment`}
            sx={{
                // Amber rather than red: this is a standing fact about where you
                // are, not an error, and a red bar on every screen stops being
                // read within a day.
                backgroundColor: '#B45309',
                color: '#FFFFFF',
                px: 2,
                py: 0.5,
                textAlign: 'center',
                position: 'sticky',
                top: 0,
                zIndex: theme => theme.zIndex.appBar + 2,
            }}
        >
            <Typography
                variant="caption"
                sx={{ fontWeight: 700, letterSpacing: '0.06em' }}
            >
                {env.label}
                {env.sharesProdData && (
                    <Typography
                        component="span"
                        variant="caption"
                        sx={{ fontWeight: 400, ml: 1, opacity: 0.95 }}
                    >
                        — this uses the REAL live data. Anything you change here
                        changes it for everyone.
                    </Typography>
                )}
            </Typography>
        </Box>
    );
};

export default EnvironmentBanner;
